import { useState, useCallback } from 'react';

interface ModalOptions {
  title?: string;
  message: string;
  type?: 'alert' | 'confirm' | 'warning';
  confirmText?: string;
  cancelText?: string;
}

export const useModal = () => {
  const [modal, setModal] = useState<ModalOptions & { isOpen: boolean; onConfirm?: () => void; onCancel?: () => void }>({
    isOpen: false,
    message: '',
  });

  const showAlert = useCallback((message: string, title?: string) => {
    return new Promise<void>((resolve) => {
      setModal({
        isOpen: true,
        message,
        title,
        type: 'alert',
        onConfirm: () => {
          resolve();
        },
      });
    });
  }, []);

  const showConfirm = useCallback((message: string, options?: { title?: string; confirmText?: string; cancelText?: string }) => {
    return new Promise<boolean>((resolve) => {
      setModal({
        isOpen: true,
        message,
        title: options?.title,
        type: 'confirm',
        confirmText: options?.confirmText,
        cancelText: options?.cancelText,
        onConfirm: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        },
      });
    });
  }, []);

  const showWarning = useCallback((message: string, options?: { title?: string; confirmText?: string; cancelText?: string }) => {
    return new Promise<boolean>((resolve) => {
      setModal({
        isOpen: true,
        message,
        title: options?.title || 'Warning',
        type: 'warning',
        confirmText: options?.confirmText || 'Close anyway',
        cancelText: options?.cancelText || 'Cancel',
        onConfirm: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        },
      });
    });
  }, []);

  const closeModal = useCallback(() => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  return {
    modal,
    showAlert,
    showConfirm,
    showWarning,
    closeModal,
  };
};

